import React from 'react';
import PropTypes from 'prop-types';

import Card from '@material-ui/core/Card';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Hidden from '@material-ui/core/Hidden';
import ReportProblemIcon from '@material-ui/icons/ReportProblemOutlined';
import AddIcon from '@material-ui/icons/Add';

import ListItemRow from '../../ListItemRow/ListItemRow';
import IndentedCardHeader from '../../IndentedCardHeader/IndentedCardHeader.js';
import IndentedCardContent from '../../IndentedCardContent/IndentedCardContent.js';
import ViewMoreLink from '../../ViewMoreLink/ViewMoreLink';
import ArrangementsHelpButton from '../../ArrangementsHelpButton/ArrangementsHelpButton';
import ArrangementsDialog from '../../ArrangementsDialog/ArrangementsDialog';
import ArrangementsInfoDialogMaintenance from '../../ArrangementsInfoDialogMaintenance/ArrangementsInfoDialog';
import ArrangementsInfoDialogRent from '../../ArrangementsInfoDialogRent/ArrangementsInfoDialog';
import FabWithText from '../../../components/FabWithText/FabWithText';
import BaseDialog from '../../BaseDialog/BaseDialog';
import ReplaceContact from '../../Replace/ReplaceContact/ReplaceContact';

import { getString, stringToCurrency } from '../../../helpers/stringHelpers.js';
import { hasRole } from '../../../helpers/authHelpers.js';
import constants from '../../../constants';
import Cookies from 'universal-cookie';

import { withStyles } from '@material-ui/core/styles';
import styles from './styles.js';

class BalanceCard extends React.Component {
  constructor(props) {
    super(props);

    const cookies = new Cookies();

    const {
      isMaintenanceBalance,
      hasRentArrangementOrDebt,
      customArrangementMessage,
    } = props;

    this.state = {
      // Show the dialog if the arrangement popup cookie is set.
      // This is set when the user logins in.
      // Custom messages however should not popup, nor should maintenance
      // popups unless there is no rent arrangement or rent debt.
      arrangementsDialogOpen:
        portalSettings.ARRANGEMENTS_POPUP_ON_LOGIN &&
        !customArrangementMessage &&
        (!isMaintenanceBalance || !hasRentArrangementOrDebt) &&
        cookies.get('myhnz_arrangement_popup')
          ? true
          : false,
      arrangementsInfoDialogOpen: false,
      arrangementsTermsDialogOpen: false,
    };

    this.toggleArrangementsDialog = this.toggleArrangementsDialog.bind(this);
    this.toggleArrangementsInfoDialog =
      this.toggleArrangementsInfoDialog.bind(this);
    this.toggleArrangementsTermsDialog =
      this.toggleArrangementsTermsDialog.bind(this);
  }

  componentDidUpdate() {
    // Remove the arrangement popup cookie when the component updates.
    // IE. When the state changes, such as a different dialog opening.
    const cookies = new Cookies();
    cookies.remove('myhnz_arrangement_popup', { path: '/' });
  }

  toggleArrangementsDialog() {
    const { gaEvent } = this.props;
    const { arrangementsDialogOpen } = this.state;
    if (arrangementsDialogOpen === false && gaEvent) {
      gaEvent();
    }
    this.setState({
      arrangementsDialogOpen: !this.state.arrangementsDialogOpen,
      arrangementsInfoDialogOpen: false,
    });
  }

  toggleArrangementsInfoDialog() {
    this.setState({
      arrangementsDialogOpen: false,
      arrangementsInfoDialogOpen: !this.state.arrangementsInfoDialogOpen,
    });
  }

  toggleArrangementsTermsDialog() {
    this.setState({
      arrangementsTermsDialogOpen: !this.state.arrangementsTermsDialogOpen,
    });
  }

  render() {
    const {
      classes,
      title,
      label,
      balance,
      icon,
      paymentReference,
      helpText,
      type,
      action,
      actionLabel,
      actionTwo,
      actionTwoLabel,
      showArrangementAction,
      isMaintenanceBalance,
      hasMaintenanceArrangement,
      hasRentArrangementOrDebt,
      arrangementFeatureDisabled,
      customArrangementMessage,
      arrangementCreateAction,
      toggleContactModal,
      gaEventDismissArrangement,
      customerRoles,
    } = this.props;

    const {
      arrangementsDialogOpen,
      arrangementsInfoDialogOpen,
      arrangementsTermsDialogOpen,
    } = this.state;

    const largeDebit =
      balance < -Math.abs(portalSettings.ARRANGEMENTS_MAXIMUM_DEBT);

    // Arrangements can only be created if:
    // 1. In debit.
    // 2. No maintenance arrangement exists.
    // 3. No rent arrangement and no rent debt (for maintenance arrangements)
    // 4. Debt can not be 'large'.
    const canCreateArrangement =
      balance < 0 &&
      !hasMaintenanceArrangement &&
      !hasRentArrangementOrDebt &&
      !largeDebit &&
      hasRole(customerRoles, constants.ROLE_ARRANGEMENT_WRITE);

    const arrangementHelpButton =
      showArrangementAction && !arrangementFeatureDisabled ? (
        <ArrangementsHelpButton action={this.toggleArrangementsDialog} />
      ) : null;

    const createArrangementButton =
      canCreateArrangement && !arrangementFeatureDisabled ? (
        <FabWithText
          label={getString('arrangements', 'NEW_ARRANGEMENT_LABEL')}
          icon={AddIcon}
          action={this.toggleArrangementsInfoDialog}
          ariaLabel="Create"
          small={true}
          iconRight={true}
        />
      ) : null;

    const ArrangementsInfoDialog = isMaintenanceBalance
      ? ArrangementsInfoDialogMaintenance
      : ArrangementsInfoDialogRent;

    const body = (
      <div className={classes.negativeTopMargin}>
        <Divider />
        <Hidden smUp>
          <div className={classes.balanceMobile}>
            <ListItemRow
              isHeader={true}
              primaryLabel=""
              secondaryLabel=""
              primaryValue={stringToCurrency(balance)}
              primaryValueAction={arrangementHelpButton}
            />
          </div>
          <Divider />
        </Hidden>
        {label && (
          <ListItemRow
            className={classes.listItem}
            primaryLabel=""
            secondaryLabel={label}
            primaryValue={
              actionLabel || action ? (
                <React.Fragment>
                  {action ? (
                    <Hidden xsDown>
                      <div className={classes.actionContainer}>
                        <ViewMoreLink action={action} label={actionLabel} />
                      </div>
                    </Hidden>
                  ) : (
                    <div className={classes.actionContainer}>
                      <Typography
                        variant="body2"
                        className={classes.actionLabel}
                        gutterBottom={false}
                      >
                        {actionLabel}
                      </Typography>
                    </div>
                  )}
                </React.Fragment>
              ) : null
            }
          />
        )}
        {paymentReference || canCreateArrangement || actionTwo ? (
          <div className={classes.paymentReferenceContainer}>
            <Divider />
            <ListItemRow
              className={classes.listItem}
              primaryLabel=""
              secondaryLabel={paymentReference ? paymentReference : null}
              primaryValue={
                actionTwo ? (
                  <Hidden xsDown>
                    <div className={classes.actionContainer}>
                      <ViewMoreLink action={actionTwo} label={actionTwoLabel} />
                    </div>
                  </Hidden>
                ) : createArrangementButton ? (
                  <Hidden xsDown>{createArrangementButton}</Hidden>
                ) : null
              }
            />
          </div>
        ) : (
          ''
        )}
        <Hidden smUp>
          {action && (
            <div className={classes.actionContainer}>
              <ViewMoreLink action={action} label={actionLabel} />
            </div>
          )}
          {actionTwo ? (
            <div className={classes.actionContainer}>
              <ViewMoreLink action={actionTwo} label={actionTwoLabel} />
            </div>
          ) : createArrangementButton ? (
            <div className={classes.actionContainer}>
              {createArrangementButton}
            </div>
          ) : null}
        </Hidden>
      </div>
    );

    return (
      <div className={classes.root}>
        {showArrangementAction && !arrangementFeatureDisabled && (
          <React.Fragment>
            <BaseDialog
              open={arrangementsTermsDialogOpen}
              icon={ReportProblemIcon}
              buttonAction={this.toggleArrangementsTermsDialog}
              buttonLabel={getString('app', 'CLOSE_LABEL')}
              title={getString('arrangements', 'TERMS_DIALOG_TITLE')}
              content={
                <ReplaceContact
                  content={portalContent.ARRANGEMENTS_TERMS}
                  action={toggleContactModal}
                  isLink={true}
                />
              }
              contentWrap={false}
              isHTML={true}
            />
            <ArrangementsDialog
              open={arrangementsDialogOpen}
              balance={balance}
              toggleAction={this.toggleArrangementsDialog}
              nextAction={this.toggleArrangementsInfoDialog}
              isMaintenanceBalance={isMaintenanceBalance}
              hasMaintenanceArrangement={hasMaintenanceArrangement}
              arrangementFeatureDisabled={arrangementFeatureDisabled}
              customArrangementMessage={customArrangementMessage}
              largeDebit={largeDebit}
              canCreateArrangement={canCreateArrangement}
              toggleContactModal={toggleContactModal}
              gaEventDismiss={gaEventDismissArrangement}
            />
          </React.Fragment>
        )}
        <ArrangementsInfoDialog
          open={arrangementsInfoDialogOpen}
          toggleAction={this.toggleArrangementsInfoDialog}
          nextAction={
            arrangementCreateAction
              ? arrangementCreateAction
              : this.toggleArrangementsInfoDialog
          }
          termsAction={this.toggleArrangementsTermsDialog}
        />

        <Card className="Card">
          <IndentedCardHeader
            title={
              <ListItemRow
                className={classes.listItem}
                isHeader={true}
                primaryLabel=""
                secondaryLabel={
                  <Typography variant="h3" className={classes.cardTitleLarge}>
                    {title}
                  </Typography>
                }
                primaryValue={stringToCurrency(balance)}
                primaryValueAction={arrangementHelpButton}
                hidePrimaryValueMobile={true}
              />
            }
            avatar={icon}
            classes={{
              title: classes.title,
              avatar: classes.avatar,
            }}
          />
          <IndentedCardContent body={body} helpText={helpText} />
        </Card>
      </div>
    );
  }
}

BalanceCard.propTypes = {
  classes: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  balance: PropTypes.number.isRequired,
  paymentReference: PropTypes.string,
  icon: PropTypes.object.isRequired,
  helpText: PropTypes.string,
  type: PropTypes.string,
  action: PropTypes.func,
  actionLabel: PropTypes.string,
  actionTwo: PropTypes.func,
  actionTwoLabel: PropTypes.string,
  showArrangementAction: PropTypes.bool,
  isMaintenanceBalance: PropTypes.bool,
  hasMaintenanceArrangement: PropTypes.bool,
  hasRentArrangementOrDebt: PropTypes.bool,
  arrangementFeatureDisabled: PropTypes.bool,
  customArrangementMessage: PropTypes.string,
  arrangementCreateAction: PropTypes.func,
  toggleContactModal: PropTypes.func,
  customerRoles: PropTypes.array,
  gaEvent: PropTypes.func,
  gaEventDismissArrangement: PropTypes.func,
};

BalanceCard.defaultProps = {
  helpText: null,
  type: null,
  paymentReference: null,
  action: null,
  actionLabel: null,
  actionTwo: null,
  actionTwoLabel: null,
  showArrangementAction: false,
  isMaintenanceBalance: false,
  hasMaintenanceArrangement: false,
  hasRentArrangementOrDebt: false,
  arrangementFeatureDisabled: false,
  customArrangementMessage: null,
  arrangementCreateAction: null,
  toggleContactModal: null,
  customerRoles: [],
  gaEvent: null,
  gaEventDismissArrangement: null,
};

export default withStyles(styles)(BalanceCard);
